import React, { useEffect, useState } from "react";

import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";

import { getTableContainerSx } from "src/shell/styles/themeUtils";

import {
  ORGANIZATIONS,
  ONBOARDING,
  AUTH,
} from "../../../platform/constants/routes";
import { EventBus } from "../../../platform/events/EventBus";
import { StateEvent } from "../../../platform/state/types";

interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  component: React.ReactNode;
  completed: boolean;
  required: boolean;
}

interface OnboardingPageProps {
  eventBus?: EventBus;
}

export const OnboardingPage: React.FC<OnboardingPageProps> = ({ eventBus }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [onboardingData, setOnboardingData] = useState({
    profileComplete: false,
    organizationSelected: false,
    preferencesSet: false,
  });

  const handleOnboardingComplete = () => {
    // Emit onboarding complete event
    if (eventBus) {
      eventBus.emitGlobal(StateEvent.ONBOARDING_COMPLETE, {
        timestamp: new Date().toISOString(),
        steps: onboardingSteps.map((step, index) => ({
          id: step.id,
          completed: completedSteps.has(index),
        })),
        data: onboardingData,
      });
    }

    // Navigate to org selection — user needs a step-2 org session to access workspace.
    void navigate(ORGANIZATIONS.LIST.path);
  };

  const [onboardingSteps] = useState<OnboardingStep[]>([
    {
      id: "welcome",
      label: "Welcome",
      description: "Welcome to Rusha! Let's get you set up.",
      component: (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Welcome to Rusha!
          </Typography>
          <Typography variant="body1" paragraph>
            We&apos;re excited to have you on board. Let&apos;s take a few
            minutes to set up your account and get you started with your first
            organization.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This setup will only take a few minutes and will help you get the
            most out of Rusha.
          </Typography>
        </Box>
      ),
      completed: false,
      required: true,
    },
    {
      id: "profile",
      label: "Complete Profile",
      description: "Tell us a bit about yourself",
      component: (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Complete Your Profile
          </Typography>
          <Typography variant="body1" paragraph>
            Please provide some basic information about yourself to personalize
            your experience.
          </Typography>
          <Button
            variant="contained"
            onClick={() => handleStepComplete("profile")}
            sx={{ mt: 2 }}
          >
            Complete Profile
          </Button>
        </Box>
      ),
      completed: false,
      required: true,
    },
    {
      id: "organization",
      label: "Organization Setup",
      description: "Create or join an organization",
      component: (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Organization Setup
          </Typography>
          <Typography variant="body1" paragraph>
            You can either create a new organization or join an existing one if
            you have an invitation.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => navigate(ONBOARDING.CREATE_ORGANIZATION.path)}
            >
              Create Organization
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(ONBOARDING.SELECT_ORGANIZATION.path)}
            >
              Join Organization
            </Button>
          </Box>
        </Box>
      ),
      completed: false,
      required: true,
    },
    {
      id: "preferences",
      label: "Preferences",
      description: "Set your preferences",
      component: (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Set Your Preferences
          </Typography>
          <Typography variant="body1" paragraph>
            Configure your notification preferences and other settings to
            customize your experience.
          </Typography>
          <Button
            variant="contained"
            onClick={() => handleStepComplete("preferences")}
            sx={{ mt: 2 }}
          >
            Set Preferences
          </Button>
        </Box>
      ),
      completed: false,
      required: false,
    },
    {
      id: "complete",
      label: "Complete Setup",
      description: "You're all set!",
      component: (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <CheckCircleIcon
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            Setup Complete!
          </Typography>
          <Typography variant="body1" paragraph>
            Congratulations! Your account is now fully set up and ready to use.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You can now access all features of Rusha and start managing your
            projects and applications.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleOnboardingComplete}
            sx={{ mt: 2 }}
          >
            Get Started
          </Button>
        </Box>
      ),
      completed: false,
      required: true,
    },
  ]);

  useEffect(() => {
    // Emit onboarding start event
    if (eventBus) {
      eventBus.emitGlobal(StateEvent.ONBOARDING_START, {
        timestamp: new Date().toISOString(),
        step: onboardingSteps[activeStep]?.id,
      });
    }
  }, [eventBus, onboardingSteps, activeStep]);

  const handleStepComplete = (stepId: string) => {
    const stepIndex = onboardingSteps.findIndex((step) => step.id === stepId);
    if (stepIndex === -1) return;

    // Mark step as completed
    setCompletedSteps((prev) => new Set([...Array.from(prev), stepIndex]));

    // Update onboarding data
    setOnboardingData((prev) => ({
      ...prev,
      [stepId === "profile"
        ? "profileComplete"
        : stepId === "organization"
          ? "organizationSelected"
          : "preferencesSet"]: true,
    }));

    // Move to next step
    if (stepIndex < onboardingSteps.length - 1) {
      setActiveStep(stepIndex + 1);
    }
  };

  const handleSkipStep = (stepIndex: number) => {
    if (onboardingSteps[stepIndex].required) {
      return; // Cannot skip required steps
    }

    setCompletedSteps((prev) => new Set([...Array.from(prev), stepIndex]));

    if (stepIndex < onboardingSteps.length - 1) {
      setActiveStep(stepIndex + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleLogout = () => {
    // Emit logout event
    if (eventBus) {
      eventBus.emitGlobal(StateEvent.LOGOUT, {
        timestamp: new Date().toISOString(),
        context: "onboarding",
      });
    }

    // Navigate to login
    void navigate(AUTH.LOGIN.path);
  };

  const getStepIcon = (stepIndex: number) => {
    if (completedSteps.has(stepIndex)) {
      return <CheckCircleIcon />;
    }

    const step = onboardingSteps[stepIndex];
    switch (step.id) {
      case "profile":
        return <PersonIcon />;
      case "organization":
        return <BusinessIcon />;
      case "preferences":
        return <SettingsIcon />;
      default:
        return null;
    }
  };

  const getProgressPercentage = () => {
    return (completedSteps.size / onboardingSteps.length) * 100;
  };

  const canCompleteOnboarding = () => {
    const requiredSteps = onboardingSteps.filter((step) => step.required);
    return requiredSteps.every(
      (_, index) =>
        onboardingSteps.findIndex((step) => step.required) === index &&
        completedSteps.has(index),
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
        bgcolor: "background.default",
      }}
    >
      {/* Main Onboarding Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 800,
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Welcome to Rusha
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Let&apos;s get you set up in just a few steps
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ width: "100%", mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Setup Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(getProgressPercentage())}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage()}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        {/* Onboarding Steps */}
        <Box sx={{ ...getTableContainerSx(theme), p: 3, width: "100%", mb: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {onboardingSteps.map((step, index) => (
              <Step key={step.id} completed={completedSteps.has(index)}>
                <StepLabel
                  icon={getStepIcon(index)}
                  optional={
                    !step.required && (
                      <Typography variant="caption" color="text.secondary">
                        Optional
                      </Typography>
                    )
                  }
                >
                  <Typography variant="h6">{step.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>{step.component}</Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleStepComplete(step.id)}
                      disabled={
                        step.id === "complete" && !canCompleteOnboarding()
                      }
                    >
                      {step.id === "complete" ? "Complete Setup" : "Continue"}
                    </Button>
                    {!step.required && (
                      <Button
                        variant="text"
                        onClick={() => handleSkipStep(index)}
                      >
                        Skip
                      </Button>
                    )}
                    {index > 0 && (
                      <Button variant="text" onClick={handleBack}>
                        Back
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Completion Status */}
        {canCompleteOnboarding() && (
          <Alert severity="success" sx={{ width: "100%", mb: 3 }}>
            All required steps completed! You can now finish the setup.
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={handleLogout} size="large">
            Logout
          </Button>

          <Button
            variant="text"
            onClick={() => navigate(ORGANIZATIONS.LIST.path)}
            size="large"
          >
            Skip Setup
          </Button>
        </Box>

        {/* Help Text */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact our support team at{" "}
            <Typography
              component="a"
              href="mailto:support@rusha.com"
              color="primary"
              sx={{ textDecoration: "none" }}
            >
              support@rusha.com
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingPage;
